import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';

import {buildEmbedUrl} from './util';

import './index.scss';
import 'react-loading-skeleton/dist/skeleton.css';

const SpotifyWidget = ({artistId, onLoad, onError, className, ...props}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const iframeRef = useRef(null);

  const embedUrl = buildEmbedUrl(artistId);

  useEffect(() => {
    if (!embedUrl) {
      const error = new Error('Invalid artist ID provided');
      setHasError(true);
      setIsLoading(false);
      setIsVisible(false);
      if (onError) {
        onError(error);
      }

      return;
    }

    setIsLoading(true);
    setHasError(false);
    setIsVisible(false);
  }, [artistId, embedUrl, onError]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
    setIsVisible(true);
    if (onLoad) {
      onLoad();
    }
  };

  const handleIframeError = () => {
    const error = new Error('Failed to load Spotify widget');
    setIsLoading(false);
    setHasError(true);
    setIsVisible(false);
    if (onError) {
      onError(error);
    }
  };

  if (!embedUrl || hasError) {
    return null;
  }

  return (
    <div className={classNames('spotify-embed-wrapper', className)} {...props}>
      {isLoading && <Skeleton height="100%" className="spotify-skeleton" />}
      <iframe
        ref={iframeRef}
        src={embedUrl}
        width="100%"
        height="100%"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
        title={`Spotify Artist Widget - ${artistId}`}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        className={classNames('spotify-embed', {
          'spotify-embed--visible': isVisible,
        })}
      />
    </div>
  );
};

SpotifyWidget.propTypes = {
  artistId: PropTypes.string.isRequired,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
  className: PropTypes.string,
};

export default SpotifyWidget;
